import type { ComponentType, ReactNode } from 'react';

export type MediaType = 'gif' | 'sticker' | 'clip';

export const INBOX_SENTINEL_PHOTO = '(In-Message Photo)';
export const INBOX_SENTINEL_GIF = '(In-Message GIF)';
export const INBOX_SENTINEL_STICKER = '(In-Message Sticker)';
export const INBOX_SENTINEL_CLIP = '(In-Message Clip)';

export type InboxSentinelRole = 'S' | 'R';

export type InboxSentinelKind = MediaType | 'photo';

export const getInboxSentinel = (type: MediaType): string => {
  switch (type) {
    case 'gif':
      return INBOX_SENTINEL_GIF;
    case 'sticker':
      return INBOX_SENTINEL_STICKER;
    case 'clip':
      return INBOX_SENTINEL_CLIP;
  }
};

export const buildInboxSentinel = (type: MediaType, role: InboxSentinelRole): string =>
  `${getInboxSentinel(type)}:${role}`;

export const buildInboxPhotoSentinel = (role: InboxSentinelRole): string =>
  `${INBOX_SENTINEL_PHOTO}:${role}`;

export const parseInboxSentinel = (
  lastText: string
): { kind: InboxSentinelKind; role?: InboxSentinelRole } | null => {
  if (!lastText) {
    return null;
  }

  let baseText = lastText;
  let role: InboxSentinelRole | undefined;

  if (lastText.length >= 3 && lastText[lastText.length - 2] === ':') {
    const roleChar = lastText[lastText.length - 1];
    if (roleChar === 'S' || roleChar === 'R') {
      role = roleChar;
      baseText = lastText.slice(0, -2);
    }
  }

  if (baseText === INBOX_SENTINEL_PHOTO) {
    return { kind: 'photo', role };
  }
  if (baseText === INBOX_SENTINEL_GIF) {
    return { kind: 'gif', role };
  }
  if (baseText === INBOX_SENTINEL_STICKER) {
    return { kind: 'sticker', role };
  }
  if (baseText === INBOX_SENTINEL_CLIP) {
    return { kind: 'clip', role };
  }

  return null;
};

export interface IMediaItem {
  Provider: string;
  Type: MediaType;
  Id: string | null;
}

export const buildMarkup = (type: MediaType, provider: string, id: string): string =>
  `[${type}:${provider}:${id}]`;

export interface IMediaShareContext {
  query?: string;
}

export interface IMediaRenderOptions {
  rounded?: boolean;
  maxWidth?: number;
}

export interface IMediaPickerProps {
  type: MediaType;
  displayName: string;
  onSelect: (item: IMediaItem, ctx?: IMediaShareContext) => void;
  onClose: () => void;
}

export interface IMediaProvider {
  key: string;
  displayName: string;
  supportedTypes: MediaType[];
  isConfigured: () => boolean;
  PickerComponent: ComponentType<IMediaPickerProps>;
  renderItem: (item: IMediaItem, options?: IMediaRenderOptions) => ReactNode;
  triggerShare: (item: IMediaItem, ctx?: IMediaShareContext) => Promise<void>;
}
